import React, { useState } from 'react';
import { getCountryCallingCode } from 'react-phone-number-input';
import PhoneInput from 'components/FormFields/PhoneInput';
import { FillInput, FillSelect, PhoneShell, Stack } from './styles';
import { inputPropTypes } from './TextInputs';

// Name, address and phone. Each part is its own full-width row: the app
// never puts fields side by side.

const objectOf = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

const part = (v, key) => (typeof v[key] === 'string' ? v[key] : '');

export const NameInput = ({
  field,
  value,
  onChange,
  labelId,
  describedBy,
  invalid,
}) => {
  const v = objectOf(value);
  const set = (key) => (e) => onChange({ ...v, [key]: e.target.value });
  const middle = !!(field.config && field.config.middle);
  return (
    <Stack
      role="group"
      aria-labelledby={labelId}
      aria-describedby={describedBy}
    >
      <FillInput
        aria-label="First name"
        placeholder="First name"
        autoComplete="off"
        value={part(v, 'first')}
        onChange={set('first')}
        $invalid={invalid}
      />
      {middle ? (
        <FillInput
          aria-label="Middle name"
          placeholder="Middle name"
          autoComplete="off"
          value={part(v, 'middle')}
          onChange={set('middle')}
        />
      ) : null}
      <FillInput
        aria-label="Last name"
        placeholder="Last name"
        autoComplete="off"
        value={part(v, 'last')}
        onChange={set('last')}
        $invalid={invalid}
      />
    </Stack>
  );
};
NameInput.propTypes = inputPropTypes;

// prettier-ignore
export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID',
  'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO',
  'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA',
  'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

const STATE_OPTIONS = US_STATES.map((s) => ({ label: s, value: s }));

export const AddressInput = ({
  value,
  onChange,
  labelId,
  describedBy,
  invalid,
}) => {
  const v = objectOf(value);
  const set = (key) => (e) => onChange({ ...v, [key]: e.target.value });
  return (
    <Stack
      role="group"
      aria-labelledby={labelId}
      aria-describedby={describedBy}
    >
      <FillInput
        aria-label="Street address"
        placeholder="Street address"
        autoComplete="off"
        value={part(v, 'street')}
        onChange={set('street')}
        $invalid={invalid}
      />
      <FillInput
        aria-label="Apt, suite, unit (optional)"
        placeholder="Apt, suite, unit (optional)"
        autoComplete="off"
        value={part(v, 'street2')}
        onChange={set('street2')}
      />
      <FillInput
        aria-label="City"
        placeholder="City"
        autoComplete="off"
        value={part(v, 'city')}
        onChange={set('city')}
        $invalid={invalid}
      />
      <FillSelect
        aria-label="State"
        placeholder="State"
        options={STATE_OPTIONS}
        value={part(v, 'state')}
        onChange={set('state')}
        $empty={!part(v, 'state')}
        $invalid={invalid}
      />
      <FillInput
        aria-label="ZIP"
        placeholder="ZIP"
        inputMode="numeric"
        autoComplete="off"
        value={part(v, 'zip')}
        onChange={set('zip')}
        $invalid={invalid}
      />
    </Stack>
  );
};
AddressInput.propTypes = inputPropTypes;

// The country to show for a calling code: the one picked here when it still
// has that code (+1 is shared by the US, Canada…), else the code itself,
// which PhoneInput resolves to its main country.
export const phoneCountry = (cc, pickedIso) => {
  const code = typeof cc === 'string' && cc ? cc : '+1';
  if (!pickedIso) return code;
  try {
    return `+${getCountryCallingCode(pickedIso)}` === code ? pickedIso : code;
  } catch (e) {
    return code;
  }
};

// International (D4): the shared PhoneInput's country picker plus the
// national number, formatted as the racer types. Stored as
// { cc: '+44', number: '770 090 012 3' }; validation strips the formatting.
export const PhoneField = ({
  value,
  onChange,
  inputId,
  describedBy,
  invalid,
  field,
}) => {
  const v = objectOf(value);
  const cc = part(v, 'cc') || '+1';
  const [pickedIso, setPickedIso] = useState(null);
  return (
    <PhoneShell $invalid={invalid}>
      <PhoneInput
        label=""
        id={inputId}
        aria-describedby={describedBy}
        aria-invalid={invalid ? true : undefined}
        aria-required={field.required ? true : undefined}
        inputMode="tel"
        autoComplete="off"
        countryCode={phoneCountry(cc, pickedIso)}
        value={part(v, 'number')}
        onChange={(number) => onChange({ cc, number })}
        onCountryChange={(code, iso) => {
          setPickedIso(iso);
          onChange({ cc: code, number: part(v, 'number') });
        }}
      />
    </PhoneShell>
  );
};
PhoneField.propTypes = inputPropTypes;
